import { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { api, clearToken, notificationApi } from '../api';
import Footer from '../components/Footer';
import InstallPrompt from './InstallPrompt';

const NAV_GROUPS = [
  {
    label: '经营概览',
    items: [{ to: '/dashboard', label: '📊 经营仪表盘', icon: '📊' }],
  },
  {
    label: '业务模块',
    items: [
      { to: '/sales', label: '🛒 销售报货', icon: '🛒' },
      { to: '/customer', label: '👥 客户报货', icon: '👥' },
      { to: '/inventory', label: '📦 进出库存', icon: '📦' },
      { to: '/production', label: '🏭 生产管理', icon: '🏭' },
      { to: '/attendance', label: '⏰ 考勤打卡', icon: '⏰' },
      { to: '/attendance-groups', label: '📍 考勤组', icon: '📍' },
    ],
  },
  {
    label: '数据管理',
    items: [
      { to: '/products', label: '📋 产品管理', icon: '📋' },
      { to: '/finance', label: '💰 财务统计', icon: '💰' },
    ],
  },
  {
    label: '消息中心',
    items: [{ to: '/notifications', label: '🔔 消息通知', icon: '🔔' }],
  },
  {
    label: '系统设置',
    items: [
      { to: '/departments', label: '🏢 部门管理', icon: '🏢' },
      { to: '/users', label: '👤 用户管理', icon: '👤' },
      { to: '/roles', label: '🔐 角色权限', icon: '🔐' },
      { to: '/logs', label: '📝 操作日志', icon: '📝' },
    ],
  },
];

// 手机底栏核心 Tab（最多 5 个，覆盖最高频业务）
const TABS = [
  { to: '/dashboard', label: '首页', icon: '📊' },
  { to: '/attendance', label: '打卡', icon: '⏰' },
  { to: '/sales', label: '报货', icon: '🛒' },
  { to: '/customer', label: '客户', icon: '👥' },
  { to: '/inventory', label: '库存', icon: '📦' },
];

export default function Layout() {
  const [user, setUser] = useState<any>(null);
  const [perms, setPerms] = useState<string[]>([]);
  const [collapsed, setCollapsed] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const [unread, setUnread] = useState(0);
  const nav = useNavigate();

  useEffect(() => {
    api('/auth/me')
      .then((d) => { setUser(d.user); setPerms(d.permissions || []); })
      .catch(() => {});
  }, []);

  // 轮询未读消息数（每 30 秒）
  useEffect(() => {
    const refresh = () => notificationApi.count().then((d: any) => setUnread(d.unread)).catch(() => {});
    refresh();
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, []);

  const logout = () => {
    clearToken();
    nav('/login');
  };

  const navCls = ({ isActive }: any) =>
    `block px-3 py-1.5 rounded-md text-sm transition ${isActive ? 'bg-brand-600 text-white font-medium' : 'text-slate-300 hover:bg-slate-700 hover:text-white'}`;

  // 铃铛按钮（桌面/手机共用）
  const Bell = () => (
    <button
      onClick={() => nav('/notifications')}
      className="relative text-2xl leading-none hover:opacity-80"
      title={`消息通知${unread ? `（${unread} 条未读）` : ''}`}
    >
      🔔
      {unread > 0 && (
        <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center font-bold">
          {unread > 99 ? '99+' : unread}
        </span>
      )}
    </button>
  );

  return (
    <div className="flex h-full">
      {/* ===== 桌面侧边栏（md 及以上） ===== */}
      <aside className={`hidden md:flex bg-slate-900 text-slate-100 flex-col transition-all duration-200 ${collapsed ? 'w-14' : 'w-56'}`}>
        <div className={`px-4 py-3 text-base font-bold border-b border-slate-700 flex items-center justify-between ${collapsed ? 'justify-center' : ''}`}>
          <div className="flex items-center gap-2">
            {!collapsed && <span>财务ERP</span>}
            {collapsed && <span>ER</span>}
            {!collapsed && <Bell />}
          </div>
          {collapsed && <Bell />}
        </div>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="mx-2 mt-1 px-2 py-1 text-xs text-slate-400 hover:text-white hover:bg-slate-700 rounded text-left"
        >
          {collapsed ? '展开 ▶' : '◀ 收缩'}
        </button>
        <nav className="flex-1 p-2 space-y-3 overflow-y-auto">
          {NAV_GROUPS.map((group) => (
            !collapsed && (
              <div key={group.label}>
                <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{group.label}</div>
                {group.items.map((n) => (
                  <NavLink key={n.to} to={n.to} className={navCls}>
                    {n.label}
                  </NavLink>
                ))}
              </div>
            ) || (
              <div key={group.label}>
                {group.items.map((n) => (
                  <NavLink key={n.to} to={n.to} title={n.label} className={({ isActive }) => `block p-2 rounded-md text-center text-lg transition ${isActive ? 'bg-brand-600' : 'text-slate-400 hover:bg-slate-700 hover:text-white'}`}>
                    {n.icon}
                  </NavLink>
                ))}
              </div>
            )
          ))}
        </nav>
        <div className={`p-3 border-t border-slate-700 ${collapsed ? 'text-center' : ''}`}>
          <div className={`text-sm truncate ${collapsed ? 'text-xs' : ''}`}>{user?.name || user?.username || '—'}</div>
          {!collapsed && (
            <button onClick={logout} className="mt-2 text-xs text-slate-300 hover:text-white w-full text-left px-1">🚪 退出登录</button>
          )}
          {collapsed && <button onClick={logout} className="mt-1 text-lg" title="退出">🚪</button>}
        </div>
      </aside>

      {/* ===== 手机顶部栏（md 以下） ===== */}
      <header className="md:hidden flex items-center justify-between bg-slate-900 text-white px-4 py-3 fixed top-0 inset-x-0 z-30">
        <span className="font-bold">财务ERP</span>
        <div className="flex items-center gap-3">
          <Bell />
          <button onClick={() => setDrawer(true)} className="text-2xl leading-none">☰</button>
        </div>
      </header>

      {/* ===== 主内容区 ===== */}
      <main className="flex-1 overflow-auto bg-slate-50/50 pt-14 md:pt-0">
        <div className="p-4 md:p-6 max-w-[1400px] mx-auto pb-24 md:pb-6">
          <Outlet />
        </div>
        <Footer />
      </main>

      {/* ===== 手机底部 Tab 栏 ===== */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-slate-200 flex justify-around items-center h-16 shadow-[0_-2px_8px_rgba(0,0,0,0.06)]">
        {TABS.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            className={({ isActive }) => `flex flex-col items-center justify-center flex-1 h-full text-xs ${isActive ? 'text-brand-600 font-semibold' : 'text-slate-400'}`}
          >
            <span className="text-xl mb-0.5">{t.icon}</span>
            {t.label}
          </NavLink>
        ))}
      </nav>

      {/* ===== 手机抽屉菜单（全部模块） ===== */}
      {drawer && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/40" onClick={() => setDrawer(false)}>
          <div className="absolute right-0 top-0 h-full w-64 bg-slate-900 text-slate-100 p-4 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <span className="font-bold">全部功能</span>
              <button onClick={() => setDrawer(false)} className="text-2xl leading-none">✕</button>
            </div>
            {NAV_GROUPS.map((group) => (
              <div key={group.label} className="mb-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-2 pb-1">{group.label}</div>
                {group.items.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    onClick={() => setDrawer(false)}
                    className={({ isActive }) => `block px-3 py-2 rounded-md text-sm ${isActive ? 'bg-brand-600 text-white' : 'text-slate-300 hover:bg-slate-700'}`}
                  >
                    {n.label}
                  </NavLink>
                ))}
              </div>
            ))}
            <button onClick={logout} className="mt-2 w-full text-left px-3 py-2 text-sm text-red-300 hover:bg-slate-700 rounded">🚪 退出登录</button>
          </div>
        </div>
      )}

      {/* PWA 安装提示（手机端自动弹出） */}
      <InstallPrompt />
    </div>
  );
}
